import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { MaskLines } from "../site/Motion";
import { EASE } from "../../lib/api";
import { HeroFrame } from "./HeroFrame";

const LINES = ["People.", "Paper.", "Pallets."];
const lineClass = (i) => (i === 1 ? "text-outline" : i === 2 ? "text-[var(--signal)]" : "");

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useSpring(useTransform(scrollYProgress, [0, 1], [0, -120]), { stiffness: 80, damping: 20 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
    ref.current.style.setProperty("--sx", `${e.clientX - r.left}px`);
    ref.current.style.setProperty("--sy", `${e.clientY - r.top}px`);
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section"
      className="grain relative min-h-[100svh] overflow-hidden bg-[var(--ink)] pt-[104px]"
      style={{ backgroundImage: "radial-gradient(520px circle at var(--sx, 70%) var(--sy, 40%), rgba(255,176,0,0.10), transparent 60%)" }}>
      <div className="grid-lines pointer-events-none absolute inset-0" />
      <div className="relative mx-auto grid max-w-[1440px] gap-10 px-5 md:px-10 lg:grid-cols-12">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.2 }}
            className="overline mb-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-[var(--bone-2)]">
            <span className="text-[var(--signal)]">BHISM Infratech Pvt. Ltd.</span>
            <span>Manpower · Records · Logistics</span>
          </motion.div>
          <MaskLines as="h1" onLoad delay={0.25} lines={LINES} lineClass={lineClass} testId="hero-headline"
            className="font-display text-[25vw] font-black uppercase leading-[0.82] tracking-[-0.01em] sm:text-[19vw] lg:text-[11.2vw] xl:text-[10.4rem]" />
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay: 0.9 }}
            className="mt-10 grid gap-8 sm:grid-cols-[1fr_auto] sm:items-end">
            <p className="max-w-md text-base text-[var(--bone-2)] md:text-lg" data-testid="hero-subtext">
              Housekeeping and technical manpower, payroll processing, scanning and digitization, transport, warehousing and storage —
              <span className="text-[var(--bone)]"> run by one accountable partner.</span>
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/quote" data-testid="hero-quote-button"
                className="group flex items-center gap-2 rounded-full bg-[var(--signal)] px-6 py-3.5 font-semibold text-[var(--ink)] transition-transform duration-300 hover:-translate-y-0.5">
                Request a quote <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
              </Link>
              <Link to="/#services" data-testid="hero-services-button"
                className="group flex items-center gap-2 rounded-full border border-white/25 px-6 py-3.5 font-semibold text-[var(--bone)] transition-colors duration-300 hover:border-[var(--signal)] hover:text-[var(--signal)]">
                Services <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
              </Link>
            </div>
          </motion.div>
        </motion.div>
        <HeroFrame mx={mx} my={my} scrollYProgress={scrollYProgress} />
      </div>
    </section>
  );
};
